import {
  useV2CreateServiceApproverMutation,
  useV2DeleteServiceApproverMutation,
} from "@generated";
import type { V2ServiceApproverResource } from "@generated";
import type { ServiceApprover } from "../types";

// Service approvers have no attributes — only `user` and `line-of-business`
// relationships (the generated type may not document them; cast through).
export const buildServiceApproverData = (
  userId: string,
  lobId: string
): V2ServiceApproverResource =>
  ({
    type: "service-approvers",
    relationships: {
      user: { data: { type: "users", id: userId } },
      "line-of-business": {
        data: { type: "line-of-businesses", id: lobId },
      },
    },
  } as unknown as V2ServiceApproverResource);

// Map the `included` array of a Line of Business GET (include=service-approvers)
// into approver rows. Approver records carry no attributes — just the user
// relationship id (resolved to a name via collaborators). Pure + unit-testable.
export const serviceApproverRowsFromIncluded = (
  included: any[] | undefined
): ServiceApprover[] =>
  (included ?? [])
    .filter((r) => r?.type === "service-approvers")
    .map((a) => ({
      id: String(a.id),
      userId: String(a.relationships?.user?.data?.id ?? ""),
      name: a.attributes?.name ?? "",
    }));

// Mutations only — the approver list is now read from the parent Line of
// Business GET's `included` (include=service-approvers), so this no longer
// runs its own list query.
export const useServiceApprovers = (lobId: string) => {
  const [create] = useV2CreateServiceApproverMutation();
  const [destroy] = useV2DeleteServiceApproverMutation();

  return {
    add: (userId: string) =>
      create({
        body: { data: buildServiceApproverData(userId, lobId) },
      }).unwrap(),
    remove: (id: string) => destroy({ id: id }).unwrap(),
  };
};

export default useServiceApprovers;
